import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πόσο χρόνο χρειάζεται να επουλωθεί ένα τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Η επιφανειακή επούλωση παίρνει συνήθως δύο με τρεις εβδομάδες, ενώ η πλήρης επούλωση στα βαθύτερα στρώματα του δέρματος μπορεί να χρειαστεί από δύο μέχρι έξι μήνες, ανάλογα με το σημείο και τον οργανισμό." } },
    { "@type": "Question", name: "Γιατί ξεφλουδίζει το τατουάζ μου;", acceptedAnswer: { "@type": "Answer", text: "Το ξεφλούδισμα είναι φυσιολογικό κομμάτι της επούλωσης και ξεκινάει συνήθως μετά την πρώτη εβδομάδα. Φεύγει το νεκρό επιφανειακό δέρμα, ενώ το πραγματικό τατουάζ μένει ασφαλές σε βαθύτερο στρώμα." } },
    { "@type": "Question", name: "Είναι φυσιολογικό να φαίνεται θαμπό το τατουάζ μου κατά την επούλωση;", acceptedAnswer: { "@type": "Answer", text: "Ναι. Μετά το ξεφλούδισμα, ένα νέο λεπτό στρώμα δέρματος καλύπτει το τατουάζ και το κάνει να φαίνεται θαμπό προσωρινά. Καθώς αυτό το δέρμα ωριμάζει, το τατουάζ ξαναβρίσκει τη λάμψη του." } },
    { "@type": "Question", name: "Μπορώ να κολυμπήσω με φρέσκο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Όχι. Απόφυγε πισίνα, θάλασσα και παρατεταμένο μούλιασμα όσο το τατουάζ επουλώνεται. Το νερό μπορεί να μαλακώσει τις φλούδες πρόωρα και να αυξήσει τον κίνδυνο μόλυνσης." } },
    { "@type": "Question", name: "Πώς ξέρω αν το τατουάζ μου έχει μολυνθεί;", acceptedAnswer: { "@type": "Answer", text: "Σημάδια μόλυνσης είναι το έντονο κοκκίνισμα που απλώνεται, το αυξανόμενο πρήξιμο, το πύον με δυσάρεστη οσμή, ο έντονος πόνος και ο πυρετός. Αν παρατηρήσεις κάτι τέτοιο, επικοινώνησε με τον tattoo artist σου ή με γιατρό." } },
    { "@type": "Question", name: "Χρειάζεται το τατουάζ ρετουσάρισμα μετά την επούλωση;", acceptedAnswer: { "@type": "Answer", text: "Μερικές φορές, ναι. Αν εμφανιστούν μικρά σημεία όπου το μελάνι δεν κράτησε πλήρως, ένα μικρό ρετουσάρισμα διορθώνει το αποτέλεσμα. Ένα καλό στούντιο τατουάζ συνήθως το προσφέρει ως μέρος της δουλειάς." } },
    { "@type": "Question", name: "Πότε φαίνεται το τελικό αποτέλεσμα ενός τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Το τατουάζ δείχνει το πλήρες, τελικό του αποτέλεσμα μετά την ολοκλήρωση της επιφανειακής επούλωσης και την ωρίμανση του νέου δέρματος, συνήθως μέσα στις πρώτες τέσσερις με έξι εβδομάδες." } },
  ],
};

export default function TattooHealingTimeAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Πόσο χρόνο χρειάζεται να επουλωθεί ένα τατουάζ;",
      "description": "Όλα τα στάδια επούλωσης ενός τατουάζ, τι να περιμένεις σε κάθε φάση, πώς να φροντίσεις σωστά το νέο σου τατουάζ και ποια λάθη πρέπει να αποφύγεις.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-06-22",
      "dateModified": "2026-06-22",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/tattoo-healing-time-athens",
      "inLanguage": "el"
    };
    inject('schema-tattoo-healing-time-faq', faqSchema);
    inject('schema-tattoo-healing-time-article', articleSchema);
    return () => {
      document.getElementById('schema-tattoo-healing-time-faq')?.remove();
      document.getElementById('schema-tattoo-healing-time-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Πόσο χρόνο χρειάζεται να επουλωθεί ένα τατουάζ; | 210 Tattoo Athens</title>
        <meta name="description" content="Όλα τα στάδια επούλωσης ενός τατουάζ, τι να περιμένεις σε κάθε φάση, πώς να φροντίσεις σωστά το νέο σου τατουάζ και ποια λάθη πρέπει να αποφύγεις." />
        <link rel="canonical" href="https://210tattoo.gr/blog/tattoo-healing-time-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/tattoo-healing-time-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/tattoo-healing-time-athens" />
        <meta property="og:title" content="Πόσο χρόνο χρειάζεται να επουλωθεί ένα τατουάζ; | 210 Tattoo Athens" />
        <meta property="og:description" content="Όλα τα στάδια επούλωσης ενός τατουάζ και πώς να φροντίσεις σωστά το νέο σου τατουάζ." />
        <meta property="og:url" content="https://210tattoo.gr/blog/tattoo-healing-time-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('tattoo-healing-time-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Πόσο χρόνο χρειάζεται να επουλωθεί ένα τατουάζ; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Οδηγός επούλωσης τατουάζ βήμα προς βήμα." />
        <meta name="twitter:image" content={ogImage('tattoo-healing-time-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Επούλωση Τατουάζ</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Πόσο χρόνο χρειάζεται</span>
              <br />
              <span className="text-white">να επουλωθεί ένα τατουάζ;</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">22 Ιουνίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Η επιφανειακή επούλωση παίρνει συνήθως δύο με τρεις εβδομάδες, αλλά η πλήρης επούλωση μπορεί να χρειαστεί μήνες. Δες τι ακριβώς συμβαίνει σε κάθε στάδιο.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* What happens */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι ακριβώς συμβαίνει <span className="text-pink-500">στο δέρμα</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η βελόνα διαπερνά το πάνω στρώμα του δέρματος, την επιδερμίδα, και τοποθετεί το μελάνι στο δεύτερο στρώμα, το χόριο, όπου μένει μόνιμα. Η διαδικασία δημιουργεί χιλιάδες μικροσκοπικές πληγές.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Το σώμα σου αντιλαμβάνεται τη διαδικασία ως τραυματισμό και ενεργοποιεί αμέσως τους μηχανισμούς επούλωσης. Αυτό εξηγεί το κοκκίνισμα, το πρήξιμο και τη ζέστη που νιώθεις τις πρώτες ώρες.
            </p>
          </div>
        </section>

        {/* Stages */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-8">Τα στάδια επούλωσης <span className="text-pink-500">ενός τατουάζ</span></h2>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Οι πρώτες μέρες, η ανοιχτή πληγή', desc: 'Από το τέλος του τατουάζ μέχρι την 3η-4η μέρα. Κοκκίνισμα, ελαφρύ πρήξιμο και έκκριμα διαυγούς υγρού είναι φυσιολογικά.' },
                { step: '02', title: 'Το ξεφλούδισμα, μέρα 4 έως 14', desc: 'Το δέρμα δημιουργεί ένα λεπτό στρώμα που ξεκολλάει σιγά σιγά. Μην ξύνεις, άσε τις φλούδες να φύγουν μόνες τους.' },
                { step: '03', title: 'Το θαμπό δέρμα, μέρα 15 έως 30', desc: 'Ένα νέο λεπτό στρώμα δέρματος κάνει το χρώμα να φαίνεται προσωρινά θαμπό. Με τις εβδομάδες ξαναβρίσκει τη λάμψη του.' },
                { step: '04', title: 'Η βαθιά επούλωση, έως μερικούς μήνες', desc: 'Τα βαθύτερα στρώματα συνεχίζουν να αναγεννώνται για δύο έως έξι μήνες, ανάλογα με τον οργανισμό και το σημείο.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* By body part */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πόσο χρόνο χρειάζεται η επούλωση <span className="text-pink-500">ανάλογα με το σημείο</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Σημεία όπως ο βραχίονας, ο πήχης και η γάμπα επουλώνονται πιο ομαλά, γιατί είναι σχετικά σταθερά. Αρθρώσεις, χέρια, δάχτυλα και πόδια κινούνται διαρκώς και μπορεί να χρειαστούν περισσότερο χρόνο. Πλευρά και μέση επηρεάζονται από την αναπνοή και την κίνηση.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Επίσης, το μέγεθος και η πολυπλοκότητα παίζουν ρόλο. Ένα μικρό, απλό σχέδιο επουλώνεται γρηγορότερα από ένα μεγάλο, γεμάτο κομμάτι με πολλά χρώματα και σκιάσεις.
            </p>
          </div>
        </section>

        {/* Factors */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Παράγοντες που επηρεάζουν <span className="text-pink-500">τον χρόνο επούλωσης</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Η γενική υγεία σου', desc: 'Καλή διατροφή, ενυδάτωση και σωστός ύπνος βοηθούν το σώμα να κάνει τη δουλειά του πιο αποτελεσματικά.' },
                { title: 'Η ηλικία και ο τύπος δέρματος', desc: 'Το νεαρότερο δέρμα τείνει να αναγεννάται γρηγορότερα. Κάποιοι τύποι δέρματος χρειάζονται περισσότερη φροντίδα.' },
                { title: 'Η ποιότητα της δουλειάς του tattoo artist', desc: 'Ένας έμπειρος καλλιτέχνης ξέρει ακριβώς πόσο βαθιά πρέπει να μπει το μελάνι, κάτι που επηρεάζει άμεσα την επούλωση.' },
                { title: 'Η φροντίδα μετά το τατουάζ', desc: 'Το aftercare είναι ο παράγοντας που ελέγχεις περισσότερο και κάνει τη μεγαλύτερη διαφορά.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Aftercare steps */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να φροντίσεις σωστά <span className="text-pink-500">το νέο σου τατουάζ</span></h2>
            <div className="space-y-4">
              {[
                { title: 'Άκουσε τον tattoo artist σου', desc: 'Οι οδηγίες του καλλιτέχνη είναι ο πρώτος και πιο αξιόπιστος οδηγός σου.' },
                { title: 'Καθάρισε απαλά την περιοχή', desc: 'Χλιαρό νερό και ήπιο σαπούνι χωρίς άρωμα, στέγνωμα ταμποναριστά.' },
                { title: 'Ενυδάτωσε σωστά', desc: 'Λεπτή στρώση ενυδατικής, χωρίς να υπερφορτώνεις το δέρμα.' },
                { title: 'Μην ξύνεις και μην τραβάς τις φλούδες', desc: 'Άσε τες να φύγουν μόνες τους για να μην βγει μελάνι μαζί τους.' },
                { title: 'Απόφυγε τον ήλιο', desc: 'Κράτα το τατουάζ μακριά από τον ήλιο όσο επουλώνεται, μετά χρησιμοποίησε πάντα αντηλιακό.' },
                { title: 'Μη βυθίζεις το τατουάζ σε νερό', desc: 'Απόφυγε πισίνα, θάλασσα και παρατεταμένο μούλιασμα. Το γρήγορο ντους είναι εντάξει.' },
                { title: 'Φόρα άνετα ρούχα', desc: 'Φαρδιά, βαμβακερά ρούχα αφήνουν την περιοχή να αναπνέει.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Signs good/bad */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Σημάδια ότι <span className="text-pink-500">όλα πάνε καλά</span></h2>
                <ul className="space-y-2 text-gray-300 text-sm leading-relaxed list-disc list-inside">
                  <li>Ελαφρύ κοκκίνισμα και πρήξιμο που υποχωρεί</li>
                  <li>Διαυγές έκκριμα τις πρώτες ημέρες</li>
                  <li>Ξεφλούδισμα και ήπια φαγούρα μετά την πρώτη εβδομάδα</li>
                  <li>Θαμπή εμφάνιση που ξαναβρίσκει τη λάμψη της</li>
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Πότε <span className="text-pink-500">να ανησυχήσεις</span></h2>
                <ul className="space-y-2 text-gray-300 text-sm leading-relaxed list-disc list-inside">
                  <li>Έντονο κοκκίνισμα που απλώνεται και χειροτερεύει</li>
                  <li>Πύον ή κιτρινοπράσινο έκκριμα με δυσάρεστη οσμή</li>
                  <li>Έντονος, αυξανόμενος πόνος μετά τις πρώτες μέρες</li>
                  <li>Πυρετός ή φουσκάλες γύρω από το τατουάζ</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-8">Συχνές <span className="text-pink-500">Ερωτήσεις</span></h2>
            <div className="space-y-4">
              {faqSchema.mainEntity.map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-2">{item.name}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θέλεις ένα τατουάζ <span className="text-pink-500">που θα επουλωθεί τέλεια;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στο 210 Tattoo Athens θα βρεις έμπειρους tattoo artists που δουλεύουν με αυστηρές συνθήκες υγιεινής και σου δίνουν αναλυτικές οδηγίες φροντίδας.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε DM στο 210 Tattoo Athens
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/sea-after-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Μπορώ να πάω θάλασσα μετά από τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πόσο πρέπει να περιμένεις και τι κινδύνους κρύβει η θάλασσα.</p>
              </Link>
              <Link to="/blog/tattoo-touch-up-guide-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πότε χρειάζεται touch up ένα τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Γιατί ξεθωριάζει ένα τατουάζ και πότε χρειάζεται ρετουσάρισμα.</p>
              </Link>
              <Link to="/blog/tatouaz-xefloudizei-fagourizei-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Τατουάζ που Ξεφλουδίζει και Φαγουρίζει;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι είναι φυσιολογικό στην επούλωση και πότε να ανησυχήσεις.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/color-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Color Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
